import dayjs from "dayjs";

export const formatInventoryExportDate = (value) => {
  if (value == null || value === "") return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : String(value);
};

const firstDefined = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return undefined;
};

const toExportNumber = (value, fallback = 0) => {
  if (value === undefined || value === null || value === "") return fallback;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? value : numeric;
};

const resolveExportSupplier = (record = {}, group = {}) =>
  firstDefined(
    record.supplierName,
    record.supplier_name,
    typeof record.supplier === "string" ? record.supplier : null,
    group.supplierName,
    group.supplier
  ) || "";

const resolveExportInvoiceNumber = (record = {}) =>
  firstDefined(record.invoiceNumber, record.invoice_number) || "";

const resolveExportExpiry = (record = {}) =>
  formatInventoryExportDate(
    firstDefined(record.expirationDate, record.expiryDate, record.expiry)
  );

const resolveExportQuantity = (record = {}, group = {}) =>
  toExportNumber(
    firstDefined(record.quantity, record.qty, group.totalQuantity, group.quantity),
    0
  );

const resolveExportCriticalLevel = (record = {}, group = {}) =>
  toExportNumber(
    firstDefined(
      record.criticalLevel,
      record.critical_level,
      group.criticalLevel,
      group.critical_level
    ),
    0
  );

const resolveExportPrice = (record = {}, group = {}) =>
  toExportNumber(firstDefined(record.price, group.price), 0);

/**
 * Pathology inventory Excel rows — mirrors table + Batch Info fields.
 * Includes Quantity and Critical Level for every batch/inventory record.
 */
export const buildPathInventoryExportTable = (groups = []) => {
  const headers = [
    "Inventory Id",
    "Name",
    "Price",
    "Quantity",
    "Critical Level",
    "Batch",
    "Expiry",
    "Supplier",
    "Invoice No",
  ];

  const tableRows = [];
  (Array.isArray(groups) ? groups : []).forEach((group) => {
    const records =
      Array.isArray(group?.records) && group.records.length > 0
        ? group.records
        : [
            {
              id: group.id,
              name: group.name,
              quantity: group.totalQuantity,
              price: group.price,
              criticalLevel: group.criticalLevel,
              supplierName: group.supplierName,
            },
          ];

    records.forEach((record) => {
      tableRows.push([
        firstDefined(record.id, group.id) ?? "",
        firstDefined(group.name, record.name) || "",
        resolveExportPrice(record, group),
        resolveExportQuantity(record, group),
        resolveExportCriticalLevel(record, group),
        firstDefined(record.batchNumber, record.batch_number) || "",
        resolveExportExpiry(record),
        resolveExportSupplier(record, group),
        resolveExportInvoiceNumber(record),
      ]);
    });
  });

  return { headers, tableRows };
};

/**
 * Medicine inventory Excel rows — mirrors table + Batch Info fields.
 * Includes Quantity for every batch/inventory record.
 */
export const buildMedicineInventoryExportTable = (groups = []) => {
  const headers = [
    "Inventory ID",
    "Name",
    "Category",
    "Type",
    "Price",
    "Quantity",
    "Batch",
    "Expiry",
    "Supplier",
    "Invoice No",
    "Manufacturer",
  ];

  const tableRows = [];
  (Array.isArray(groups) ? groups : []).forEach((group) => {
    const records =
      Array.isArray(group?.records) && group.records.length > 0
        ? group.records
        : [
            {
              id: group.id,
              name: group.name,
              quantity: group.totalQuantity,
              price: group.price,
              supplierName: group.supplier,
              manufacturer: group.manufacturer,
            },
          ];

    records.forEach((record) => {
      tableRows.push([
        firstDefined(record.id, group.id) ?? "",
        firstDefined(group.name, record.name) || "",
        firstDefined(group.medicineType, record.medicineType) || "",
        firstDefined(group.medicineDetails, record.medicineDetails) || "",
        resolveExportPrice(record, group),
        resolveExportQuantity(record, group),
        firstDefined(record.batchNumber, record.batch_number) || "",
        resolveExportExpiry(record),
        resolveExportSupplier(record, group),
        resolveExportInvoiceNumber(record),
        firstDefined(record.manufacturer, group.manufacturer) || "",
      ]);
    });
  });

  return { headers, tableRows };
};
